/**
 * RequestorSummary — one requestor's identity and everywhere they are assigned (OPEN-053).
 *
 * The shared body behind two surfaces: the `RequestorDetail` PANE (Sites & Operators tab, with
 * a back button) and the `RequestorDetailModal` POPUP (Job Board's Requestor column, 86bazkeyn).
 * Extracted rather than duplicated — two copies of "everywhere this person is assigned" would
 * drift, and the slot semantics are the part that must not.
 *
 * Read-only by construction. Editing is offered only where the caller passes `onEdit`, which
 * the Job Board popup deliberately does not: editing someone's record from a job row is a
 * permission question that has not been answered yet, and inventing one here would answer it
 * by accident.
 */
import * as React from 'react';
import { Pencil } from 'lucide-react';
import { useRequestorAssignments, type Requestor } from './hooks';
import { capForRender, capNotice } from './list-scale';
import { ActiveBadge, Pill, rowStyle, searchInputStyle, subText } from '../sites-operators/ui-bits';

const sectionTitle: React.CSSProperties = {
  fontSize: '13px',
  fontWeight: 700,
  color: 'var(--color-text-tertiary)',
  margin: '18px 0 8px',
};

export interface RequestorSummaryProps {
  readonly requestor: Requestor;
  /** Omit to render without any edit affordance (the Job Board popup does). */
  readonly onEdit?: (() => void) | undefined;
}

export function RequestorSummary({ requestor, onEdit }: RequestorSummaryProps): React.JSX.Element {
  const { data, isLoading, isError } = useRequestorAssignments(requestor.id);
  const [filter, setFilter] = React.useState('');

  const all = data?.assignments ?? [];
  const needle = filter.trim().toLowerCase();
  const matching = needle
    ? all.filter(
        (a) =>
          (a.name ?? '').toLowerCase().includes(needle) ||
          (a.customer ?? '').toLowerCase().includes(needle) ||
          (a.sub ?? '').toLowerCase().includes(needle),
      )
    : all;
  const capped = capForRender(matching);
  const notice = capNotice(capped, 'locations');

  const contact =
    [requestor.company, requestor.phone, requestor.email].filter((v) => v !== null).join(' · ') ||
    'No contact details';

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
        <h2 style={{ fontSize: '18px', fontWeight: 700 }}>{requestor.name}</h2>
        {requestor.company !== null && <Pill label={requestor.company} tone="accent" />}
        <ActiveBadge active={requestor.isActive} />
        {onEdit !== undefined && (
          <button
            type="button"
            aria-label="Edit requestor"
            onClick={onEdit}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: 'var(--color-text-secondary)',
            }}
          >
            <Pencil size={14} aria-hidden="true" />
          </button>
        )}
      </div>
      <p style={subText}>{contact}</p>

      <div style={sectionTitle}>Assigned locations {data ? `(${all.length})` : ''}</div>

      {isLoading && <p style={subText}>Loading…</p>}
      {isError && (
        <p style={{ ...subText, color: 'var(--color-danger)' }}>Could not load assignments.</p>
      )}

      {!isLoading && !isError && all.length === 0 && (
        <p style={subText}>
          Not assigned anywhere yet. Use “Assign to locations…” on the requestors list to put this
          person on sites and wells.
        </p>
      )}

      {all.length > 0 && (
        <>
          {/* Only worth a filter once the list is long enough to need one. */}
          {all.length > 10 && (
            <input
              value={filter}
              onChange={(e): void => setFilter(e.target.value)}
              placeholder="Filter assigned locations…"
              aria-label="Filter assigned locations"
              style={searchInputStyle}
            />
          )}

          {matching.length === 0 && <p style={subText}>No assigned locations match that filter.</p>}

          {capped.shown.map((a) => (
            <div key={`${a.targetType}:${a.targetId}:${a.slot}`} style={rowStyle}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontWeight: 600 }}>{a.name ?? '(unnamed)'}</span>
                  <Pill label={a.targetType === 'WELL' ? 'Well' : 'Site'} tone="neutral" />
                </div>
                <div style={subText}>
                  {[a.customer, a.sub].filter((v) => v !== null).join(' · ')}
                </div>
              </div>
              {/* Which of the two slots they hold here — the thing the reverse view exists to
                  show, since a person can be slot 1 on one site and slot 2 on another. */}
              <Pill label={`Requestor ${a.slot}`} tone="accent" />
            </div>
          ))}

          {notice !== null && <p style={{ ...subText, marginTop: '8px' }}>{notice}</p>}
        </>
      )}
    </div>
  );
}
